import React from 'react';
import { Card, Form } from 'react-bootstrap';

interface PaginationWithInfoProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  showItemsPerPage?: boolean;
  itemsPerPageOptions?: number[];
  className?: string;
}

/**
 * Componente de paginação com indicador de informações
 * Estilo IDÊNTICO ao ReservasList - usando Bootstrap e CSS inline
 */
const Pagination: React.FC<PaginationWithInfoProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  itemsPerPageOptions = [5, 10, 20, 50],
  className = ''
}) => {
  // Não renderiza se não houver items suficientes
  if (totalItems <= itemsPerPage) return null;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  // Calcula páginas do meio - LÓGICA IDÊNTICA ao ReservasList
  const getMiddlePages = (): number[] => {
    const pages: number[] = [];
    
    for (let i = 0; i < Math.min(6, totalPages - 2); i++) {
      let pageNum: number;
      
      if (totalPages <= 7) {
        pageNum = i + 2;
      } else if (currentPage <= 4) {
        pageNum = i + 2;
      } else if (currentPage >= totalPages - 3) {
        pageNum = totalPages - 5 + i;
      } else {
        pageNum = currentPage - 2 + i;
      }
      
      if (pageNum > 1 && pageNum < totalPages) {
        pages.push(pageNum);
      }
    }

    return pages;
  };

  const middlePages = getMiddlePages();
  const showJumpForward = totalPages > 7 && currentPage < totalPages - 3;

  return (
    <Card className={`border-0 shadow-sm ${className}`} style={{ borderRadius: '16px' }}>
      <Card.Body className="p-4">
        <div className="d-flex flex-column align-items-center gap-3">
          {/* Indicador "Mostrando X/Y" - IDÊNTICO ao ReservasList */}
          <div className="text-center">
            <span style={{ color: '#0EA5E9', fontWeight: '500', fontSize: '14px' }}>
              Mostrando: {startIndex + 1}-{endIndex}/{totalItems}
            </span>
          </div>

          {/* Navegação de páginas - IDÊNTICA ao ReservasList */}
          <div className="d-flex align-items-center" style={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px',
            padding: '4px',
            backgroundColor: 'white'
          }}>
            {/* Primeira página */}
            <button
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              style={{
                padding: '10px 16px',
                border: 'none',
                backgroundColor: currentPage === 1 ? '#0EA5E9' : 'transparent',
                color: currentPage === 1 ? 'white' : '#0EA5E9',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                minWidth: '44px'
              }}
            >
              1
            </button>

            {/* Páginas do meio */}
            {middlePages.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  backgroundColor: currentPage === pageNum ? '#0EA5E9' : 'transparent',
                  color: currentPage === pageNum ? 'white' : '#0EA5E9',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  margin: '0 3px',
                  minWidth: '44px'
                }}
              >
                {pageNum}
              </button>
            ))}

            {/* Botão de avanço rápido */}
            {showJumpForward && (
              <button
                onClick={() => onPageChange(Math.min(currentPage + 5, totalPages))}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: '#0EA5E9',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  margin: '0 3px',
                  minWidth: '44px'
                }}
              >
                »
              </button>
            )}

            {/* Última página */}
            {totalPages > 1 && (
              <button
                onClick={() => onPageChange(totalPages)}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  backgroundColor: currentPage === totalPages ? '#0EA5E9' : 'transparent',
                  color: currentPage === totalPages ? 'white' : '#0EA5E9',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  minWidth: '44px'
                }}
              >
                {totalPages}
              </button>
            )}
          </div>

          {/* Seletor de itens por página - IDÊNTICO ao ReservasList */}
          {showItemsPerPage && onItemsPerPageChange && (
            <div className="d-flex align-items-center gap-2">
              <small style={{ color: '#666', fontSize: '13px' }}>Itens por página:</small>
              <Form.Select
                value={itemsPerPage}
                onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                style={{ 
                  width: '70px', 
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#0EA5E9'
                }}
                className="btn-sm"
              >
                {itemsPerPageOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default Pagination;

